/**
 * Component that handles the multiple Save buttons in the composer.
 */
import dayjs from 'dayjs'
import dayjsTimezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import styled from 'styled-components'

// Local imports
import { ButtonsQueuingTypesMap, SaveButtonTypes } from '../../AppConstants'

// Component imports
import styles from '../css/UpdateSaver.module.css'

// Local component imports
import { ButtonStyled } from './style'
import type { UpdateSaverProps } from './types'
import { getFormattedWhatPreventsSavingMessages } from './utils'
import AppActionCreators from '../../action-creators/AppActionCreators'
import { InlineScheduler } from './InlineScheduler'
import { useAppSelector } from '~publish/legacy/store'
import { selectContentGenerationStatus } from '~publish/legacy/ai/state/slice'
import { ConditionalTooltip } from '../ConditionalTooltip'

dayjs.extend(utc)
dayjs.extend(dayjsTimezone)

const ActionButtons = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
`

export const UpdateSaverForApprovals = (
  props: UpdateSaverProps,
): JSX.Element | null => {
  const {
    appState,
    userData,
    metaData,
    timezone,
    saveButtons,
    moreThanOneProfileSelected,
    isPinnedToSlot,
    sentPost,
    draftMode,
    whatPreventsSavingMessages: preventsSavingMessages = [],
    whatPreventsSavingDraftMessages = [],
    requiresApprovalForSelectedProfiles,
  } = props
  const { scheduledAt } = props

  const { contentGenerationInProgress } = useAppSelector((state) =>
    selectContentGenerationStatus(state),
  )

  // Prevent unexpected use cases, we have already checked this outside of this component
  if (sentPost || draftMode) return null

  const { isSavingDraftPossible, isDraftsSavePending } = appState

  const isSaveDisabled = !isSavingDraftPossible || contentGenerationInProgress

  if (saveButtons.length === 0) return null

  const inlineButtonsWrapperClassName = [
    isDraftsSavePending
      ? styles.savePendingInlineButtonsWrapper
      : styles.inlineButtonsWrapper,
  ].join(' ')

  const tootltipMessage = contentGenerationInProgress
    ? 'Generating content...'
    : getFormattedWhatPreventsSavingMessages({
        warningMessages: preventsSavingMessages,
      })

  const handleDraftAction = (buttonType: {
    id: keyof typeof SaveButtonTypes
  }): void => {
    const queuingType = ButtonsQueuingTypesMap.get(buttonType.id)

    AppActionCreators.saveDrafts(queuingType, {
      shouldSkipEmptyTextAlert: false,
      saveDraftsFromQueue: true,
      hasCampaignsToTagsFeature: true,
      approvalMode:
        buttonType.id === SaveButtonTypes.ADD_TO_DRAFTS_REQUESTING_APPROVAL
          ? buttonType.id
          : undefined,
    })
  }

  const saveDraftLabel = moreThanOneProfileSelected
    ? 'Save as Drafts'
    : 'Save as Draft'

  // only when the user requires approval for all profiles, we can request approval directly
  const isRequestApprovalDisabled =
    requiresApprovalForSelectedProfiles !== 'all'
  const requestAppovalMessage =
    isRequestApprovalDisabled && !isSaveDisabled
      ? `Some of your selected channels do not require approval. Use "${saveDraftLabel}" first and you can request approval later if needed.`
      : null

  return (
    <section className={styles.section} data-testid="update-saver-buttons">
      <InlineScheduler
        scheduledAt={scheduledAt}
        timezone={timezone}
        userData={userData}
        metaData={metaData}
        draftMode={draftMode}
        isPinnedToSlot={isPinnedToSlot}
        shouldDisplayTime={true}
        allowRemoveSchedule={true}
        // As we are only creating drafts in approval mode, we don't need to show the slot picker
        isSlotPickingAvailable={false}
        doSelectedProfilesHaveSlots={false}
        availableSchedulesSlotsForDay={[]}
      />

      <ActionButtons
        data-testid="draft-approval-buttons-section"
        className={inlineButtonsWrapperClassName}
        data-disabled={isSaveDisabled}
      >
        <ConditionalTooltip content={isSaveDisabled ? tootltipMessage : null}>
          <ButtonStyled
            data-testid="save-draft-and-request-approval"
            disabled={isSaveDisabled}
            onClick={(): void =>
              handleDraftAction({
                id: SaveButtonTypes.ADD_TO_DRAFTS,
              })
            }
            type="secondary"
            label={saveDraftLabel}
          />

          <ConditionalTooltip content={requestAppovalMessage}>
            <ButtonStyled
              data-testid="save-and-request-approval"
              disabled={isSaveDisabled || isRequestApprovalDisabled}
              onClick={(): void =>
                handleDraftAction({
                  id: SaveButtonTypes.ADD_TO_DRAFTS_REQUESTING_APPROVAL,
                })
              }
              type="primary"
              label="Request Approval"
            />
          </ConditionalTooltip>
        </ConditionalTooltip>
      </ActionButtons>
    </section>
  )
}
